import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GET, POST } from '../../config/api';
import { routes } from '../../config/config';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import styles from './SignupScreen.module.css';

const usernameRegex = /^[a-zA-Z0-9_.]+$/;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Simple country list (you can expand this or use a library)
const countries = [
  { code: 'FR', name: 'France' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  // Add more countries as needed
];

const SignupScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const possibleEmail = location.state?.email || '';

  const [username, setUsername] = useState({ value: '', error: false });
  const [firstName, setFirstName] = useState({ value: '', error: false });
  const [lastName, setLastName] = useState({ value: '', error: false });
  const [email, setEmail] = useState({ value: possibleEmail, error: false });
  const [password, setPassword] = useState({ value: '', error: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [universities, setUniversities] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [major, setMajor] = useState('');
  const [countryCode, setCountryCode] = useState('FR');
  const [error, setError] = useState('');

  useEffect(() => {
    getUniversities();
  }, []);

  const getUniversities = async () => {
    const response = await GET('/universities', { signUp: true });
    if (response.status === 200 && response.data) {
      setUniversities(response.data.universities || []);
      if (response.data.universities && response.data.universities.length > 0) {
        setSelectedUniversity(response.data.universities[0]);
        if (response.data.universities[0].majors && response.data.universities[0].majors.length > 0) {
          setMajor(response.data.universities[0].majors[0]);
        }
      }
    } else if (response.error) {
      setError(response.message || 'Failed to load universities. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    let isValid = true;

    // Validate username
    if (username.value.length < 3) {
      setUsername({ ...username, error: true });
      isValid = false;
    } else if (!usernameRegex.test(username.value)) {
      setError('Username must be letters, numbers, underscores and dots');
      setUsername({ ...username, error: true });
      isValid = false;
    } else {
      setUsername({ ...username, error: false });
    }

    // Validate first name
    if (firstName.value.length < 3) {
      setFirstName({ ...firstName, error: true });
      isValid = false;
    } else {
      setFirstName({ ...firstName, error: false });
    }

    // Validate last name
    if (lastName.value.length < 3) {
      setLastName({ ...lastName, error: true });
      isValid = false;
    } else {
      setLastName({ ...lastName, error: false });
    }

    // Validate email
    if (email.value.length < 6 || !emailRegex.test(email.value)) {
      setEmail({ ...email, error: true });
      isValid = false;
    } else {
      setEmail({ ...email, error: false });
    }

    // Validate password
    if (password.value.length < 8) {
      setPassword({ ...password, error: true });
      isValid = false;
    } else {
      setPassword({ ...password, error: false });
    }

    if (!isValid) {
      return;
    }

    setIsSubmitting(true);

    const response = await POST('/auth/signup', {}, {
      username: username.value,
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      password: password.value,
      university: selectedUniversity?._id,
      major,
      countryCode,
      fcmToken: '', // Web doesn't use FCM tokens
    });

    setIsSubmitting(false);

    if (response.status === 200 || response.status === 201) {
      if (response.data) {
        // Navigate to OTP screen
        navigate(routes.otp, { state: { email: email.value } });
      }
    } else if (response.error) {
      if (response.status === 409 && response.message === 'Email already exists') {
        setError('Account already exists. Please login to continue.');
        setTimeout(() => {
          navigate(routes.loginEmail, { state: { email: email.value } });
        }, 2000);
      } else {
        setError(response.message || 'Unable to sign up. Please try again later.');
      }
    } else {
      setError('Unable to sign up. Please try again later.');
    }
  };

  const handleUniversityChange = (university) => {
    setSelectedUniversity(university);
    if (university.majors && university.majors.length > 0) {
      setMajor(university.majors[0]);
    } else {
      setMajor('');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBackIcon className={styles.backIcon} />
        </button>
        <h1 className={styles.title}>Sign Up</h1>
      </div>

      <div className={styles.content}>
        <div className={styles.terms}>
          <p>
            By continuing, you agree to our{' '}
            <a
              href="https://tellmiz.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              terms of service
            </a>{' '}
            and{' '}
            <a
              href="https://tellmiz.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              privacy policy
            </a>
            .
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.errorMessage}>{error}</div>}

          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>Nationality</label>
            <FormControl fullWidth className={styles.select}>
              <Select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                displayEmpty
                sx={{
                  color: 'white',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                  },
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  height: '52px',
                }}
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {selectedUniversity && (
            <div className={styles.inputContainer}>
              <label className={styles.inputLabel}>University</label>
              <FormControl fullWidth className={styles.select}>
                <Select
                  value={selectedUniversity._id || ''}
                  onChange={(e) => {
                    const university = universities.find((u) => u._id === e.target.value);
                    handleUniversityChange(university);
                  }}
                  displayEmpty
                  sx={{
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.6)',
                    },
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    height: '52px',
                  }}
                >
                  {universities.map((university) => (
                    <MenuItem key={university._id} value={university._id}>
                      {university.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}

          {major && selectedUniversity?.majors && selectedUniversity.majors.length > 0 && (
            <div className={styles.inputContainer}>
              <label className={styles.inputLabel}>Major</label>
              <FormControl fullWidth className={styles.select}>
                <Select
                  value={major}
                  onChange={(e) => setMajor(e.target.value)}
                  displayEmpty
                  sx={{
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.6)',
                    },
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    height: '52px',
                  }}
                >
                  {selectedUniversity.majors.map((majorOption) => (
                    <MenuItem key={majorOption} value={majorOption}>
                      {majorOption}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}

          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>Username</label>
            <input
              type="text"
              className={`${styles.textInput} ${username.error ? styles.invalidInput : ''}`}
              placeholder="Enter your username"
              value={username.value}
              onChange={(e) => setUsername({ value: e.target.value, error: false })}
              disabled={isSubmitting}
              autoCapitalize="off"
              required
            />
          </div>

          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>First Name</label>
            <input
              type="text"
              className={`${styles.textInput} ${firstName.error ? styles.invalidInput : ''}`}
              placeholder="Enter your first name"
              value={firstName.value}
              onChange={(e) => setFirstName({ value: e.target.value, error: false })}
              disabled={isSubmitting}
              autoCapitalize="words"
              required
            />
          </div>

          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>Last Name</label>
            <input
              type="text"
              className={`${styles.textInput} ${lastName.error ? styles.invalidInput : ''}`}
              placeholder="Enter your last name"
              value={lastName.value}
              onChange={(e) => setLastName({ value: e.target.value, error: false })}
              disabled={isSubmitting}
              autoCapitalize="words"
              required
            />
          </div>

          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>Email</label>
            <input
              type="email"
              className={`${styles.textInput} ${email.error ? styles.invalidInput : ''}`}
              placeholder="Enter your email"
              value={email.value}
              onChange={(e) => setEmail({ value: e.target.value, error: false })}
              disabled={isSubmitting}
              autoCapitalize="off"
              required
            />
          </div>

          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>Password</label>
            <div className={`${styles.passwordContainer} ${password.error ? styles.invalidInput : ''}`}>
              <input
                type={passwordVisible ? 'text' : 'password'}
                className={styles.passwordInput}
                placeholder="Enter your password"
                value={password.value}
                onChange={(e) => setPassword({ value: e.target.value, error: false })}
                disabled={isSubmitting}
                autoCapitalize="off"
                required
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setPasswordVisible(!passwordVisible)}
              >
                {passwordVisible ? (
                  <VisibilityOffIcon className={styles.eyeIcon} />
                ) : (
                  <VisibilityIcon className={styles.eyeIcon} />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={`${styles.signUpButton} ${isSubmitting ? styles.disabled : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Signing up...' : 'Sign Up'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignupScreen;

















